import { useState, useEffect, useRef } from "react";
import mermaid from "mermaid";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { db } from "../../../firebaseConfig";
import "./Mermaid.css";
import { makeId } from "../../utils/id";

mermaid.mermaidAPI.initialize({
  startOnLoad: false,
  theme: "base",
  themeVariables: {
    background: "#272a2c",
    primaryColor: "#363e44",
    primaryTextColor: "#fafaf9",
    lineColor: "#f8f7f6",
  },
});

const Mermaid = ({
  id,
  content,
  onEditing,
  editable,
  onFocus,
  onBlur,
  viewId,
}) => {
  const [diagramCode, setDiagramCode] = useState(content);
  const [viewMode, setViewMode] = useState("visual"); // 'visual' or 'code'
  const [isAddingLine, setIsAddingLine] = useState(false);
  const [fromNode, setFromNode] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [editingNode, setEditingNode] = useState(null);
  const [currentChartType, setCurrentChartType] = useState("");
  const mermaidRef = useRef(null);
  const plusButtonRef = useRef(null);
  const textInputRef = useRef(null);
  const shapeOptions = ["[]", "()", "{}", "<>", "([])", "(())"];
  const chartTypes = ["graph TD", "graph LR", "sequenceDiagram", "gantt"];

  useEffect(() => {
    const firstLine = diagramCode.trim().split("\n")[0];
    if (chartTypes.includes(firstLine)) {
      setCurrentChartType(firstLine);
    } else {
      setCurrentChartType("graph TD");
    }
  }, [diagramCode]);

  const addNodeAndLink = (fromNodeId) => {
    const newNodeId = makeId(4);
    const newNode = `${newNodeId}[New Node]`;
    const newLink = `${fromNodeId} --> ${newNodeId}`;
    setDiagramCode(`${diagramCode}\n${newNode}\n${newLink}`);
  };

  const updateNodeLabel = (nodeId, newLabel) => {
    const newCode = diagramCode
      .split("\n")
      .map((line) => {
        const nodeRegex = new RegExp(
          `^(\\s*)${nodeId}(\\[.*?\\]|\\(.*?\\)|\\{.*?\\}|<.*?>)`
        );
        const match = line.match(nodeRegex);
        if (match) {
          const shape = match[2];
          return `${match[1]}${nodeId}${shape[0]}${newLabel}${
            shape[shape.length - 1]
          }`;
        }
        return line;
      })
      .join("\n");
    setDiagramCode(newCode);
  };

  useEffect(() => {
    const render = async () => {
      if (viewMode === "visual" && mermaidRef.current) {
        if (plusButtonRef.current) {
          plusButtonRef.current.remove();
          plusButtonRef.current = null;
        }
        if (textInputRef.current) {
            textInputRef.current.remove();
            textInputRef.current = null;
        }
        try {
          const graphId = "mermaid-graph-" + id;
          const { svg } = await mermaid.mermaidAPI.render(graphId, diagramCode);
          mermaidRef.current.innerHTML = svg;

          if (selectedNode) {
            const nodeElement = mermaidRef.current.querySelector(
              `g[id$="${selectedNode}"]`
            );
            if (nodeElement) {
              nodeElement.classList.add("selected-node");

              const bbox = nodeElement.getBBox();
              const plusButton = document.createElement("div");
              plusButton.innerHTML = "+";
              plusButton.className = "plus-button";
              plusButton.style.left = `${bbox.x + bbox.width + 10}px`;
              plusButton.style.top = `${bbox.y + bbox.height / 2 - 10}px`;
              plusButton.onclick = () => addNodeAndLink(selectedNode);

              mermaidRef.current.appendChild(plusButton);
              plusButtonRef.current = plusButton;
            }
          }

          if (editingNode) {
            const nodeElement = mermaidRef.current.querySelector(
                `g[id$="${editingNode}"]`
              );
            if (nodeElement) {
                const textElement = nodeElement.querySelector('text, .nodeLabel');
                if(textElement) {
                    const bbox = textElement.getBBox();
                    const input = document.createElement('input');
                    input.type = 'text';
                    input.value = textElement.textContent;
                    input.style.position = 'absolute';
                    input.style.left = `${bbox.x}px`;
                    input.style.top = `${bbox.y}px`;
                    input.style.width = `${bbox.width}px`;
                    input.style.height = `${bbox.height}px`;
                    input.style.border = 'none';
                    input.style.background = 'transparent';
                    input.style.textAlign = 'center';


                    input.onblur = () => {
                        updateNodeLabel(editingNode, input.value);
                        setEditingNode(null);
                    }

                    input.onkeydown = (e) => {
                        if(e.key === 'Enter') {
                            updateNodeLabel(editingNode, input.value);
                            setEditingNode(null);
                        }
                    }
                    
                    mermaidRef.current.appendChild(input);
                    input.focus();
                    textInputRef.current = input;
                }
            }
          }

        } catch (e) {
          console.error("Mermaid rendering error:", e);
          if (mermaidRef.current) {
            mermaidRef.current.innerHTML = `<pre>${diagramCode}</pre>`;
          }
        }
      }
    };
    render();
  }, [viewMode, diagramCode, id, selectedNode, editingNode]);

  useEffect(() => {
    const handleDiagramClick = (e) => {
      let target = e.target;
      let nodeId = null;
      let isTextClick = false;

      while (target && target.parentElement) {
        if (target.tagName === 'text' || target.classList.contains('nodeLabel')) {
            isTextClick = true;
        }
        if (target.classList.contains("node")) {
          const idMatch =
            target.id.match(/flowchart-([A-Za-z0-9]+)/) ||
            target.id.match(/g[ro]up-([A-Za-z0-9]+)/);
          if (idMatch && idMatch[1]) {
            nodeId = idMatch[1];
            break;
          }
        }
        target = target.parentElement;
      }

      if (nodeId) {
          if (isAddingLine) {
            if (!fromNode) {
              setFromNode(nodeId);
            } else {
              if (fromNode !== nodeId) {
                const newLine = `${fromNode} --> ${nodeId}`;
                setDiagramCode(diagramCode + "\n" + newLine);
              }
              setFromNode(null);
              setIsAddingLine(false);
            }
          } else {
            if (selectedNode === nodeId && isTextClick) {
                setEditingNode(nodeId);
            } else {
                setSelectedNode(nodeId);
            }
          }
      } else {
        setSelectedNode(null);
        setEditingNode(null);
      }
    };

    const container = mermaidRef.current;
    if (viewMode === "visual" && container) {
      container.addEventListener("click", handleDiagramClick);
    }

    return () => {
      if (container) {
        container.removeEventListener("click", handleDiagramClick);
      }
    };
  }, [isAddingLine, fromNode, diagramCode, viewMode, selectedNode]);

  const handleCodeChange = (e) => {
    setDiagramCode(e.target.value);
  };

  const handleSave = async () => {
    if (onEditing) {
      onEditing();
    }
    const viewRef = doc(db, "views", viewId);
    try {
      const viewSnap = await getDoc(viewRef);
      if (viewSnap.exists()) {
        const contents = viewSnap.data().contents;
        const componentIndex = contents.findIndex((c) => c.id === id);
        if (componentIndex > -1) {
          contents[componentIndex].content = diagramCode;
          await updateDoc(viewRef, { contents });
        }
      }
    } catch (error) {
      console.error("Error saving mermaid diagram:", error);
    }

    if (onBlur) {
      onBlur();
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (diagramCode !== content) {
        handleSave();
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [diagramCode, content]);

  const addNode = () => {
    const nodeId = makeId(4);
    const newNode = `${nodeId}[New Node]`;
    setDiagramCode(diagramCode + "\n" + newNode);
  };

  const toggleAddLineMode = () => {
    setIsAddingLine(!isAddingLine);
    setFromNode(null);
    setSelectedNode(null);
  };

  const changeShape = (shape) => {
    if (!selectedNode) return;

    const newCode = diagramCode
      .split("\n")
      .map((line) => {
        const nodeRegex = new RegExp(
          `^(\\s*)${selectedNode}(\\[.*?\\]|\\(.*?\\)|\\{.*?\\}|<.*?>)`
        );
        const match = line.match(nodeRegex);

        if (match) {
          const textMatch = line.match(/\[(.*?)\]|\((.*?)\)|\{(.*?)\}|<(.*?)>/);
          const text = textMatch
            ? textMatch[1] || textMatch[2] || textMatch[3] || textMatch[4]
            : " ";

          let open, close;
          switch (shape) {
            case "()":
              open = "(";
              close = ")";
              break;
            case "{}":
              open = "{";
              close = "}";
              break;
            case "<>":
              open = "<";
              close = ">";
              break;
            default:
              open = "[";
              close = "]";
          }

          return `${match[1]}${selectedNode}${open}${text}${close}`;
        }
        return line;
      })
      .join("\n");

    setDiagramCode(newCode);
  };

  const removeSelected = () => {
    if (!selectedNode) return;

    const newCode = diagramCode
      .split("\n")
      .filter((line) => {
        const isNodeDefinition = line.trim().startsWith(selectedNode);
        const isLink =
          line.includes(`--> ${selectedNode}`) ||
          line.includes(`${selectedNode} -->`);
        return !isNodeDefinition && !isLink;
      })
      .join("\n");

    setDiagramCode(newCode);
    setSelectedNode(null);
  };

  const moveNode = (direction) => {
    if (!selectedNode) return;
    const lines = diagramCode.split("\n");
    const nodeLineIndex = lines.findIndex((line) =>
      line.trim().startsWith(selectedNode)
    );
    if (nodeLineIndex === -1) return;

    const newIndex = direction === "up" ? nodeLineIndex - 1 : nodeLineIndex + 1;
    if (newIndex < 0 || newIndex >= lines.length) return;

    const newLines = [...lines];
    const [movedLine] = newLines.splice(nodeLineIndex, 1);
    newLines.splice(newIndex, 0, movedLine);
    setDiagramCode(newLines.join("\n"));
  };

  const changeChartType = (type) => {
    if (
      window.confirm(
        "Changing the chart type will delete all your current work. Are you sure you want to continue?"
      )
    ) {
      let sampleCode = `${type}\n`;
      switch (type) {
        case "gantt":
          sampleCode += `
                title A Gantt Diagram
                dateFormat  YYYY-MM-DD
                section Section
                A task           :a1, 2014-01-01, 30d
                Another task     :after a1  , 20d
                section Another
                Task in sec      :2014-01-12  , 12d
                another task      : 24d`;
          break;
        case "sequenceDiagram":
          sampleCode += `
                Alice->>John: Hello John, how are you?
                John-->>Alice: Great!
                Alice-)John: See you later!`;
          break;
        default: // flowchart
          sampleCode += `
                A[Start] --> B{Is it?};
                B -- Yes --> C[OK];
                C --> D[Rethink];
                D --> A;
                B -- No --> E[End];
            `;
      }
      setDiagramCode(sampleCode);
    }
  };

  return (
    <div className="mermaid-container">
      {editable && (
        <div className="mermaid-toolbar">
          <div className="toggle-switch">
            <button
              onClick={() => setViewMode("visual")}
              className={viewMode === "visual" ? "active" : ""}
            >
              Visual
            </button>
            <button
              onClick={() => setViewMode("code")}
              className={viewMode === "code" ? "active" : ""}
            >
              Code
            </button>
          </div>
          {viewMode === "visual" && (
            <div className="creator-interface">
              <select
                value={currentChartType}
                onChange={(e) => changeChartType(e.target.value)}
              >
                <option value="">Change Chart Type</option>
                {chartTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              <button onClick={addNode}>Add Node</button>
              <button
                onClick={toggleAddLineMode}
                className={isAddingLine ? "active" : ""}
              >
                {isAddingLine ? "Cancel Add Line" : "Add Line"}
              </button>

              {selectedNode && (
                <>
                  <select onChange={(e) => changeShape(e.target.value)}>
                    <option value="">Change Shape</option>
                    {shapeOptions.map((shape) => (
                      <option key={shape} value={shape}>
                        {shape}
                      </option>
                    ))}
                  </select>
                  <button onClick={() => moveNode("up")}>Move Up</button>
                  <button onClick={() => moveNode("down")}>Move Down</button>
                  <button onClick={removeSelected}>Remove Selected</button>
                </>
              )}
              {isAddingLine && !fromNode && <span>Click the first node</span>}
              {isAddingLine && fromNode && <span>Click the second node</span>}
            </div>
          )}
        </div>
      )}

      {viewMode === "code" && editable ? (
        <textarea
          className="mermaid-editor"
          value={diagramCode}
          onChange={handleCodeChange}
          autoFocus
        />
      ) : (
        <div
          ref={mermaidRef}
          className="mermaid"
          key={diagramCode} // Force re-render when code changes
        ></div>
      )}
    </div>
  );
};

export default Mermaid;
